import * as React from 'react';
import { useQuery } from 'react-query';
import { Calculator } from 'lucide-react';
import { Scaffold } from 'layouts';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { Button } from 'components/ui/button';
import { DataTableV2, type DataTableV2Ref } from 'components/ui/DataTableV2';
import { useAuth } from 'hooks';
import { usePermission } from 'hooks/usePermission';
import { useViagensBilling } from 'hooks/api/viagens-billing';
import { useViagensBillingSearchParams } from 'hooks/store/viagens-billing';
import { notify } from 'services/notifications';
import type { ViagemBilling, ViagensBillingFilters } from '../types';
import { PageTitle } from '../components/page-title';
import { TabbedNavigation } from '../components/tabbed-navigation';
import { ViagensBillingFilter } from './components/viagens-billing-filter';
import { useViagensBillingColumns } from './hooks';
import { ViagensBillingActivitiesDialog } from './viagens-billing-activities-dialog';
import { ViagensBillingDetailDialog } from './viagens-billing-detail-dialog';
import { exportViagensBillings } from './utils';

const QUERY_KEY = 'fetch-paginated-viagens-billings';

const clearBodyPointerLock = () => {
  [0, 50, 150, 300].forEach((delay) => {
    window.setTimeout(() => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }, delay);
  });
};

export const ViagensBillingListPage = () => {
  const tableRef = React.useRef<DataTableV2Ref>(null);
  const { profile } = useAuth();
  const { hasPermissionTo } = usePermission();
  const { fetchBillings, fetchBillingById } = useViagensBilling();
  const [selectedBilling, setSelectedBilling] =
    React.useState<ViagemBilling | null>(null);
  const [activitiesBilling, setActivitiesBilling] =
    React.useState<ViagemBilling | null>(null);
  const [reportExportLoading, setReportExportLoading] = React.useState(false);
  const [filterLoadingAction, setFilterLoadingAction] = React.useState<
    'filter' | 'reset' | null
  >(null);
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearBillingsState,
  } = useViagensBillingSearchParams({ persisted: true });

  const billingsQuery = useQuery({
    queryKey: [QUERY_KEY, filters, paginationControl],
    queryFn: () =>
      fetchBillings({
        filters,
        pagination: paginationControl,
      }),
    refetchOnWindowFocus: false,
  });

  const billingDetailsQuery = useQuery({
    queryKey: [
      'fetch-viagens-billing-hours',
      billingsQuery.data?.data.map((billing) => billing.id) ?? [],
    ],
    queryFn: async () =>
      Promise.all(
        (billingsQuery.data?.data ?? []).map((billing) =>
          fetchBillingById(billing.id)
        )
      ),
    enabled: Boolean(billingsQuery.data?.data.length),
    refetchOnWindowFocus: false,
  });

  const billingsWithDetails = React.useMemo(() => {
    const detailsById = new Map(
      (billingDetailsQuery.data ?? []).map((billing) => [billing.id, billing])
    );

    return (billingsQuery.data?.data ?? []).map((billing) => ({
      ...billing,
      billingItems: detailsById.get(billing.id)?.billingItems ?? billing.billingItems,
    }));
  }, [billingDetailsQuery.data, billingsQuery.data?.data]);

  const handleFilters = React.useCallback(
    (nextFilters: ViagensBillingFilters) => {
      setFilterLoadingAction('filter');
      updateFilters(nextFilters);
      updatePaginationControl({ page: 1 });
    },
    [updateFilters, updatePaginationControl]
  );

  const handleResetFilters = React.useCallback(() => {
    setFilterLoadingAction('reset');
    clearBillingsState();
  }, [clearBillingsState]);

  const columns = useViagensBillingColumns({
    onShowDetails: setSelectedBilling,
    onShowActivities: setActivitiesBilling,
  });
  const canRemoveEstadiaFromBilling = hasPermissionTo([
    'REMOVE_ESTADIA_CONTROLE',
  ]);
  const canReverseEstadiaBilling = hasPermissionTo([
    'ESTORNO_ESTADIA_CONTROLE',
  ]);

  React.useEffect(() => {
    if (!selectedBilling && !activitiesBilling) {
      clearBodyPointerLock();
    }
  }, [activitiesBilling, selectedBilling]);

  React.useEffect(() => {
    if (filterLoadingAction && !billingsQuery.isFetching) {
      setFilterLoadingAction(null);
    }
  }, [billingsQuery.isFetching, filterLoadingAction]);

  const handleExistingBillingsReport = React.useCallback(async () => {
    const total = billingsQuery.data?.meta?.total ?? 0;

    if (!total) {
      notify('error', 'Não existem faturas para exportar.');
      return;
    }

    try {
      setReportExportLoading(true);
      const billings = await fetchBillings({
        filters,
        pagination: {
          page: 1,
          limit: total,
        },
      });

      const detailedBillings = await Promise.all(
        billings.data.map((billing) => fetchBillingById(billing.id))
      );

      await exportViagensBillings(detailedBillings, ['xlsx'], profile?.name);
      notify('success', 'Relatório das faturas existentes gerado com sucesso.');
    } catch (error: any) {
      notify('error', error?.message || 'Não foi possível exportar faturas.');
    } finally {
      setReportExportLoading(false);
    }
  }, [
    billingsQuery.data?.meta?.total,
    fetchBillings,
    fetchBillingById,
    filters,
    profile?.name,
  ]);

  return (
    <Scaffold>
      <TabbedNavigation />
      <ViagensBillingDetailDialog
        billing={selectedBilling}
        canRemoveEstadiaFromBilling={canRemoveEstadiaFromBilling}
        canReverseEstadiaBilling={canReverseEstadiaBilling}
        onOpenChange={(open) => !open && setSelectedBilling(null)}
      />
      <ViagensBillingActivitiesDialog
        billing={activitiesBilling}
        onOpenChange={(open) => !open && setActivitiesBilling(null)}
      />

      <div className="flex flex-col gap-6">
        <PageHeaderForFilters
          headerTitle={<PageTitle subtitle="Faturas" />}
          className="[&>div:first-child]:items-center"
        >
          <ViagensBillingFilter
            onSubmitFilters={handleFilters}
            onResetFilters={handleResetFilters}
            filterLoadingAction={filterLoadingAction}
          />
        </PageHeaderForFilters>

        <DataTableV2
          ref={tableRef}
          data={billingsWithDetails}
          columns={columns}
          manualPagination
          noBreakHeader
          inlineTopBarControls
          onPaginationChange={updatePaginationControl}
          isLoading={billingsQuery.isLoading}
          tableWrapperClassName="max-h-[calc(100vh-330px)]"
          rightPinnedColumns={['acoes']}
          renderTopBar={() => (
            <div className="ml-auto inline-flex h-fit w-fit shrink-0 rounded-md bg-white p-0">
              <Button
                type="button"
                size="sm"
                className="h-9 bg-emerald-600 px-5 font-GilroySemibold text-white hover:bg-emerald-700"
                disabled={
                  reportExportLoading || !billingsQuery.data?.meta?.total
                }
                onClick={handleExistingBillingsReport}
              >
                <Calculator className="mr-2 h-4 w-4" />
                Exportar
              </Button>
            </div>
          )}
          pagination={{
            ...billingsQuery.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </div>
    </Scaffold>
  );
};
